import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCompanySearch } from '@/queries/people';
import { useKbFeatureFlags } from '@/queries/kb';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { SearchResults } from '@/features/ticket-admin/SearchResults';
import type { CompanySearchResult } from '@/types/ticket';

export interface KbVisibilityCompany {
  _id: string;
  name?: string;
}

export function KbVisibilityFields({
  companies,
  onCompaniesChange,
  featureFlags,
  onFeatureFlagsChange
}: {
  companies: KbVisibilityCompany[];
  onCompaniesChange: (next: KbVisibilityCompany[]) => void;
  featureFlags: string[];
  onFeatureFlagsChange: (next: string[]) => void;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query);
  const search = useCompanySearch(debounced);
  const flags = useKbFeatureFlags();

  const pickedIds = new Set(companies.map(c => c._id));
  const results = search.data?.filter(c => !pickedIds.has(c._id));
  // Saved codes the API no longer lists stay visible so they can still be unticked.
  const knownCodes = new Set((flags.data ?? []).map(f => f.code));
  const flagOptions = [...(flags.data ?? []), ...featureFlags.filter(code => !knownCodes.has(code)).map(code => ({ code, label: code }))];

  const pick = (c: CompanySearchResult) => {
    onCompaniesChange([...companies, { _id: c._id, name: c.name }]);
    setQuery('');
  };

  const toggleFlag = (code: string, checked: boolean) =>
    onFeatureFlagsChange(checked ? [...featureFlags, code] : featureFlags.filter(f => f !== code));

  return (
    <fieldset className="space-y-4 rounded-etn-menu border border-etn-border-hairline p-4">
      <legend className="px-1 text-sm font-semibold">Who can see this</legend>

      <div className="space-y-2">
        <Label htmlFor="kb-visibility-company">Companies</Label>
        {companies.length > 0 && (
          <ul aria-label="Selected companies" className="flex flex-wrap gap-1.5">
            {companies.map(c => (
              <li
                key={c._id}
                className="inline-flex items-center gap-1 rounded-etn-pill border border-etn-border-hairline bg-etn-control-subtle py-0.5 pl-2.5 pr-1 text-etn-micro text-etn-text-cell"
              >
                {c.name || c._id}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Remove ${c.name || c._id}`}
                  onClick={() => onCompaniesChange(companies.filter(x => x._id !== c._id))}
                >
                  <X />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <Input id="kb-visibility-company" autoComplete="off" placeholder="Search companies" value={query} onChange={e => setQuery(e.target.value)} />
        <SearchResults
          label="Matching companies"
          term={query}
          items={results}
          isFetching={search.isFetching || query !== debounced}
          onPick={pick}
          renderItem={c => <span className="text-etn-text-cell">{c.name || c._id}</span>}
        />
        <p className="text-etn-micro text-etn-text-soft">Leave empty for any company. Any features ticked below still apply.</p>
      </div>

      <div className="space-y-2">
        <span className="text-sm font-medium">Features</span>
        {flags.isLoading && <p className="text-etn-micro text-etn-text-soft">Loading features…</p>}
        {flags.error && <p className="text-etn-micro text-etn-danger-text">Could not load features.</p>}
        {flagOptions.length > 0 && (
          <ul className="grid gap-1.5 sm:grid-cols-2">
            {flagOptions.map(f => (
              <li key={f.code}>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={featureFlags.includes(f.code)}
                    onChange={e => toggleFlag(f.code, e.target.checked)}
                  />
                  {f.label}
                </label>
              </li>
            ))}
          </ul>
        )}
        <p className="text-etn-micro text-etn-text-soft">
          Only companies with at least one of these features can see it, on top of any companies picked above. Leave empty for no feature requirement.
        </p>
      </div>
    </fieldset>
  );
}
